<template>
    <article>
        <p>
            Nowadays, in  the era of microservices, infrastructures have become super-complex:
            dynamic nodes provisioning, autoscaling, dozens or even hundreds of containers working side by side.
            In order to maintain control over such infrastructure, we need to be able to know
            what has happened to each application at any given time.
        </p>

        <p>
            First, let's look at computing resources.
            Usually, when engineers talk about resources, they are actually referring to utilization which is not always correct.
            For example, the high utilization of a CPU by a container is not an issue in general.
            The real problem here is that this can cause another container on the same machine to perform slower due to a lack of CPU time.
        </p>

        <p>
            Only a few people know that the Linux kernel counts exactly how long each task has been waiting for
            kernel resources to become available.
            For instance, a task could wait for CPU time or synchronous block I/O to complete.
            Such delays usually directly affect application latency, so measuring this seems quite reasonable.
        </p>

        <p>
            When researching how we could detect CPU-related issues, we came to the conclusion that the CPU delay
            of a container is a perfect starting point for investigation.
            The kernel provides per-PID or per-TGID <a href="https://www.kernel.org/doc/html/latest/accounting/delay-accounting.html" target="_blank" rel="noopener noreferrer">statistics</a>:
         </p>

        <ul>
            <li>The per-PID (Process ID) statistics correspond only to the main thread of a process.</li>
            <li>The per-TGID (Task Group ID) statistics are the sum over all threads of a process.</li>
        </ul>

        <p>
            <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">Node-agent</a>
            gathers per-TGID statistics from the kernel through the <a href="https://man7.org/linux/man-pages/man7/netlink.7.html" target="_blank" rel="noopener noreferrer">Netlink</a> protocol and aggregates it to the per-container metrics:
        </p>

        <v-img max-width="80%" class="ma-auto mb-6" src="/static/img/blog/delay_accounting_aggregation.svg" />

        <p>
            Let's see how one of the resulting metrics can help in detecting various CPU-related issues.
        </p>

        <h2>A noisy neighbor</h2>

        <p>
            In this scenario, a CPU-intensive container (<var>stats-aggregator</var>) runs on the
            same node as a latency-sensitive app (<var>reservations</var>).
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/linux-delay-accounting/slos.png" />
        </v-card>

        <p>
            In the chart above, we can see that the <var>reservations</var> app is not meeting its SLOs: requests are performing slowly
            (the red area) and failing (the black area).
            Now let's look at the CPU metrics related to this application:
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/linux-delay-accounting/cpu.png" />
        </v-card>

        <p>
            Comparing these metrics, we can easily conclude that the <var>stats-aggregator</var> has caused the issue.
            Yet, could we do such analysis without using the <MetricLink name="container_resources_cpu_delay_seconds_total" exporter="node-agent"/> metric?
        </p>

        <p>
            On the contrary, here are the same metrics for a case where <var>stats-aggregator-(be)</var> has started with the lowest CPU priority
            (the <a href="https://kubernetes.io/docs/tasks/configure-pod-container/quality-service-pod/#create-a-pod-that-gets-assigned-a-qos-class-of-besteffort" target="_blank" rel="noopener noreferrer">Best-Effort</a> QOS class was assigned).
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/linux-delay-accounting/best-effort.png" />
        </v-card>

        <p>
            As you can see, the CPU utilization of the node is 100%, but this is not significantly affecting the <var>reservations</var>' SLIs.
        </p>

        <p>
            At Coroot, we came up with the following algorithm to detect CPU-related issues of a particular application:
        </p>

        <ul>
            <li>
                If the app is meeting its SLOs, it's not necessary to check anything else.
            </li>
            <li>
                If the total CPU delay of all containers of the app is correlating with the affected SLIs of this app,
                this means the app is experiencing a lack of CPU time.
            </li>
        </ul>

        <p>
            I must clarify why checking correlation can often be tricky.
            As seen in the first scenario above, several SLIs (latency and success rate) were affected all at once.
            This is why Coroot also checks the correlation between the metric and the composition of the relevant SLIs.
        </p>

        <p>
            In the first stage, we had an issue with the application response time,
            so there was a strong correlation between this SLI and the total CPU delay.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/linux-delay-accounting/correlation-1.png" />
        </v-card>

        <p>
            After the errors appeared, the correlation between <var>cpu_delay</var> and the response time became weak.
            Yet, the correlation between the sum of errors and slow requests was strong enough to assure that
            the container did indeed experience a lack of CPU time.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/linux-delay-accounting/correlation-2.png" />
        </v-card>

        <p>
            A lack of CPU time can be caused by several reasons:
        </p>

        <ul>
            <li>
                A container competes for CPU time against the other containers running on the same node.
            </li>
            <li>
                The Linux CPU scheduler (CFS) <a href="https://tempesta-tech.com/blog/nginx-tail-latency#the-linux-scheduler" target="_blank" rel="noopener noreferrer">preempts</a> the processes of a container in favor of the other containers on the same node.
            </li>
            <li>
                A container consumes all available CPU time on its own.
            </li>
            <li>
                A container has reached its CPU limit and has been throttled by the system.
            </li>
        </ul>

        <h2>CPU throttling</h2>

        <p>
            If a container is limited in CPU time (throttled), the <var>cpu_delay</var> metric is bound to increase.
            However, in this case, we have a separate metric that measures how long each container has been throttled — <MetricLink name="container_resources_cpu_throttled_seconds_total" exporter="node-agent"/>.
            Thus, Coroot uses it in addition to <var>cpu_delay</var> to determine that a container has reached its CPU
            limit and it is that which caused a lack of CPU time.
        </p>

        <p>
            Here is a failure scenario where the number of requests to the <var>image-resizer</var> app is gradually increased until its containers
            reach their CPU limits.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/linux-delay-accounting/throttling.png" />
        </v-card>

        <p>
            Detection also works by checking the correlation between the affected SLIs of the app and <var>throttled_time</var>.
            Thus, if the correlation is strong, we can conclude that the cause of a CPU lack is throttling.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/linux-delay-accounting/correlation-3.png" />
        </v-card>

        <h2>Useful links</h2>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'cpu'}}">CPU Inspection <v-icon dense>mdi-help-circle-outline</v-icon></router-link>
            </li>
        </ul>


    </article>
</template>

<script>
import MetricLink from "@/components/MetricLink";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
</style>
